import React, { FC, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { FormGroup, Grid, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { CreateCrmUserRequestDto } from '@sparling/financial-common'
import { routes } from '../../../../constants/routes'
import { InputField } from '../../../../components/InputField'
import { InputSelect } from '../../../../components/InputSelect'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { TransparentButton } from '../../../../components/Buttons/TransparentButton'
import { InputSwitch } from '../../../../components/Buttons/InputSwitch'
import { useRest } from '../../../../components/Context'
import { CRM_USER_ROLES } from '../../../../constants/crmUserRoles'
import { InputPhone } from '../../../../components/InputPhone'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
  },
  title: {
    lineHeight: 0,
  },
  buttonWrap: {
    justifyContent: 'space-between',
    width: 256,
  },
  formContent: {
    justifyContent: 'space-evenly',
    width: '100%',
    margin: '32px 32px 32px 0',
    padding: 32,
    background: 'rgba(216, 216, 216, 0.05)',
    height: 560,
  },
  inputWrap: {
    width: 632,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  switchWrap: {
    width: 275,
    color: 'rgba(255,255,255,0.72)',
    '& label': {
      justifyContent: 'space-between',
      paddingBottom: 10,
      marginLeft: 0,
    },
  },
}))

type Inputs = {
  firstName: string
  lastName: string
  email: string
  phone: string
  password: string
  confirmPassword: string
  slack: string
  crmRole: string
}

const resolver = classValidatorResolver(CreateCrmUserRequestDto)

export const CreateCrmUser: FC = () => {
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver,
  })

  const classes = useStyles()
  const { rxios } = useRest()
  const history = useHistory()
  const { handleSubmit } = formMethods

  const onSubmit = useCallback(async (data: Inputs) => {
    rxios.post('crm-user', data).subscribe(
      // eslint-disable-next-line
      (next: any) => {},
      // eslint-disable-next-line
      (error: any) => {
        toast.error(error.response.data.message)
      },
      () => {
        toast.success(`User ${data.firstName} ${data.lastName} was created`)
        history.push(routes.SETTINGS_ADMIN_USERS)
      },
    )
    // eslint-disable-next-line
  }, [])

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.topWrap}>
          <Typography variant="h1" className={classes.title}>
            Create CRM User
          </Typography>
          <Grid container item className={classes.buttonWrap}>
            <TransparentButton width={120} onClick={history.goBack} text="Cancel" />
            <GreenButton type="submit" width={120} text="Save" />
          </Grid>
        </Grid>
        <Grid item container className={classes.formContent} direction={'column'}>
          <Grid container className={classes.inputWrap}>
            <InputField inputName={'firstName'} labelName={'First Name'} inputType={'text'} />
            <InputField inputName={'lastName'} labelName={'Last Name'} inputType={'text'} />
            <InputField inputName={'email'} labelName={'Email'} inputType={'email'} />
            <InputPhone />
            <InputField inputName={'password'} labelName={'Password'} inputType={'password'} />
            <InputField inputName={'confirmPassword'} labelName={'Confirm Password'} inputType={'password'} />
            <InputField inputName={'slack'} labelName={'Slack'} inputType={'text'} />
            <InputSelect
              inputName={'crmRole'}
              labelName={'Role'}
              placeholder={'Please Select'}
              width={300}
              enums={CRM_USER_ROLES}
            />
          </Grid>

          <FormGroup className={classes.switchWrap}>
            <InputSwitch label={'Two-Factor Authentication:'} />
            <InputSwitch label={'Force Password Reset:'} />
          </FormGroup>
        </Grid>
      </form>
    </FormProvider>
  )
}
